import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Wordmark } from "./Wordmark";

const NAV_ITEMS = [
  { id: "nav-events", label: "The Season", href: "#events" },
  { id: "nav-manifesto", label: "Manifesto", href: "#manifesto" },
  { id: "nav-lifestyle", label: "Lifestyle", href: "#lifestyle" },
  { id: "nav-press", label: "Press", href: "#press" },
  { id: "nav-journal", label: "Journal", href: "#journal" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  // Route-aware click: if not on landing, jump home first (carrying the hash);
  // if on landing, let SmoothScroll's document-level handler take over.
  const handleHashClick = (e, href) => {
    if (!href || !href.startsWith("#")) return;
    if (location.pathname === "/") return; // let SmoothScroll handle it
    e.preventDefault();
    setOpen(false);
    navigate({ pathname: "/", hash: href });
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Scroll-spy: watch each section, mark the one that's currently the
  // "dominant" section in view. Uses IntersectionObserver with a rootMargin
  // that biases toward sections whose top is roughly under the nav bar.
  useEffect(() => {
    const ids = NAV_ITEMS.map((i) => i.href.slice(1));
    const targets = ids
      .map((id) => document.getElementById(id))
      .filter(Boolean);
    if (targets.length === 0) return;

    // Track per-section intersection ratio; pick the highest.
    const ratios = new Map();
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          ratios.set(e.target.id, e.isIntersecting ? e.intersectionRatio : 0);
        });
        let best = "";
        let bestRatio = 0;
        ratios.forEach((r, id) => {
          if (r > bestRatio) {
            best = id;
            bestRatio = r;
          }
        });
        if (bestRatio > 0) {
          setActive(best);
          // URL hash sync: only on the landing route, use replaceState so we
          // don't stack history entries or trigger router navigation.
          if (
            typeof window !== "undefined" &&
            window.location.pathname === "/"
          ) {
            const nextHash = "#" + best;
            if (window.location.hash !== nextHash) {
              window.history.replaceState(
                null,
                "",
                window.location.pathname + window.location.search + nextHash
              );
            }
          }
        }
      },
      {
        rootMargin: "-96px 0px -55% 0px",
        threshold: [0, 0.2, 0.4, 0.6, 0.8, 1],
      }
    );
    targets.forEach((t) => io.observe(t));
    return () => io.disconnect();
  }, []);

  return (
    <>
      <motion.header
        data-testid="site-nav"
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, delay: 0.3, ease: [0.76, 0, 0.24, 1] }}
        className={`fixed top-0 left-0 right-0 z-[9999] pointer-events-auto transition-colors duration-500 ${
          scrolled
            ? "bg-[color:var(--bt-bg)]/85 backdrop-blur-xl border-b border-[color:var(--bt-gold-line)]"
            : "bg-transparent"
        }`}
      >
        <div className="mx-auto max-w-[1600px] px-6 md:px-10 py-5 flex items-center justify-between">
          <Link
            to="/"
            data-testid="nav-home-link"
            onClick={() => {
              if (location.pathname === "/") {
                if (typeof window !== "undefined") {
                  if (window.__lenis) {
                    window.__lenis.scrollTo(0, { immediate: false });
                  } else {
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }
                }
              }
            }}
            className="flex items-center gap-3"
          >
            <Wordmark size="sm" dataTestId="nav-wordmark" />
          </Link>

          <nav className="hidden md:flex items-center gap-10">
            {NAV_ITEMS.map((item) => {
              const isActive = active === item.href.slice(1);
              return (
                <a
                  key={item.id}
                  data-testid={item.id}
                  data-active={isActive ? "true" : "false"}
                  href={item.href}
                  onClick={(e) => handleHashClick(e, item.href)}
                  className={`group relative text-[11px] tracking-widest-brand uppercase transition-colors cursor-pointer ${
                    isActive
                      ? "text-[color:var(--bt-gold)]"
                      : "text-[color:var(--bt-ink-dim)] hover:text-[color:var(--bt-ink)]"
                  }`}
                >
                  {item.label}
                  <span
                    className={`absolute -bottom-1.5 left-0 h-px bg-[color:var(--bt-gold)] transition-all duration-500 ${
                      isActive ? "w-full opacity-100" : "w-0 group-hover:w-full"
                    }`}
                  />
                </a>
              );
            })}
          </nav>

          <div className="hidden md:flex items-center gap-6">
            <a
              data-testid="nav-signin"
              href="#signin"
              onClick={(e) => handleHashClick(e, "#signin")}
              className="text-[11px] tracking-widest-brand uppercase text-[color:var(--bt-ink-dim)] hover:text-[color:var(--bt-gold)] transition-colors"
            >
              Sign In
            </a>
            <a
              data-testid="nav-request-access"
              href="#access"
              onClick={(e) => handleHashClick(e, "#access")}
              className="bt-btn"
            >
              Request Access
            </a>
          </div>

          <button
            data-testid="nav-mobile-toggle"
            aria-label="Menu"
            className="md:hidden text-[color:var(--bt-ink)]"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-panel"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-[color:var(--bt-bg)]/95 backdrop-blur-2xl md:hidden pt-24 px-8"
          >
            <div className="flex flex-col gap-8">
              {NAV_ITEMS.map((item) => {
                const isActive = active === item.href.slice(1);
                return (
                  <a
                    key={item.id}
                    href={item.href}
                    data-testid={`mobile-${item.id}`}
                    data-active={isActive ? "true" : "false"}
                    onClick={(e) => {
                      setOpen(false);
                      handleHashClick(e, item.href);
                    }}
                    className={`font-serif-display text-4xl hover:text-[color:var(--bt-gold)] transition-colors ${
                      isActive
                        ? "text-[color:var(--bt-gold)]"
                        : "text-[color:var(--bt-ink)]"
                    }`}
                  >
                    {item.label}
                  </a>
                );
              })}
              <div className="pt-6 border-t border-[color:var(--bt-gold-line)]">
                <a
                  data-testid="mobile-request-access"
                  href="#access"
                  onClick={(e) => {
                    setOpen(false);
                    handleHashClick(e, "#access");
                  }}
                  className="bt-btn w-full"
                >
                  Request Access
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Nav;
